import React, { useContext } from 'react';

import styled from 'styled-components';

import { getUserName } from '@libs/NwUsers';

import { ListContext } from '@contexts/ListContext';

import NwIcon from '@components/Gui/NwIcon';

import SeasonModelNote from './SeasonModelNote';
import { isSeasonModelCancelled } from './showSeasonsHelpers';
import { useShowSeasonFilterContext } from './ShowSeasonFilterContext';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const SeasonNameRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.35rem 0.85rem;
    font-size: ${(props) => props.theme.FontSizeSm};
    text-transform: uppercase;
    letter-spacing: 0.03em;
    line-height: 1.2;
    margin-bottom: 0.35rem;
    color: ${(props) => (props.$onCard ? '#fff' : props.theme.PrimaryColor)};
    font-weight: 600;

    .season-item,
    .booker-item {
        display: inline-flex;
        align-items: center;
        gap: 0.35rem;
    }
`;

const TableWrapper = styled.div`
    margin-top: 0.65rem;
    max-width: 40rem;

    &.cancelled {
        opacity: 0.55;

        .booker-item {
            text-decoration: line-through;
            color: ${(props) => props.theme.DangerColor};
        }
    }
`;

const CardWrapper = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 5;
    width: 100%;
    padding: 0.45rem 0.55rem;
    background: ${(props) => props.theme.PrimaryColor}E6;
    color: #fff;
    text-transform: none;
    font-size: ${(props) => props.theme.FontSizeSm};
    line-height: 1.25;

    &.cancelled {
        opacity: 0.75;

        .booker-row {
            text-decoration: line-through;
        }
    }

    .booker-row {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        text-transform: uppercase;
        margin-bottom: 0.25rem;
        font-weight: 600;
    }
`;

const ShowSeasonModelWallInfo = ({ modelId, variant = 'table' }) => {
    const { cachedList } = useContext(ListContext);
    const { seasonId, seasonName, seasonModelsByModelId, isLoadingSeasonDetails } = useShowSeasonFilterContext();

    if (!seasonId || isLoadingSeasonDetails) {
        return null;
    }

    const seasonModel = seasonModelsByModelId.get(Number(modelId));
    if (!seasonModel) {
        return null;
    }

    const isCancelled = isSeasonModelCancelled(seasonModel);
    const hasBooker = !!seasonModel.BookerID;
    const bookerName = hasBooker ? getUserName(seasonModel.BookerID, cachedList.users.items) : null;
    const hasNotes = !!seasonModel.Notes;
    const isCard = variant === 'card';

    const stopRowClick = (event) => {
        event.stopPropagation();
    };

    const tableHeaderRow = seasonName || hasBooker ? (
        <SeasonNameRow $onCard={false} onClick={stopRowClick} onMouseDown={stopRowClick}>
            {seasonName ? (
                <span className="season-item">
                    <NwIcon icon={byPrefixAndName.fasl['calendar-star']} />
                    <span>{seasonName}</span>
                </span>
            ) : null}
            {hasBooker ? (
                <span className="booker-item">
                    <NwIcon icon={byPrefixAndName.fasl['circle-user']} />
                    <span>{bookerName}</span>
                </span>
            ) : null}
        </SeasonNameRow>
    ) : null;

    const cardSeasonNameRow = seasonName ? (
        <SeasonNameRow $onCard onClick={stopRowClick} onMouseDown={stopRowClick}>
            <NwIcon icon={byPrefixAndName.fasl['calendar-star']} />
            <span>{seasonName}</span>
        </SeasonNameRow>
    ) : null;

    if (isCard) {
        return (
            <CardWrapper className={isCancelled ? 'cancelled' : ''} onClick={stopRowClick} onMouseDown={stopRowClick}>
                {cardSeasonNameRow}
                {hasBooker ? (
                    <div className="booker-row" onClick={stopRowClick} onMouseDown={stopRowClick}>
                        <NwIcon icon={byPrefixAndName.fasl['circle-user']} />
                        <span>{bookerName}</span>
                    </div>
                ) : null}
                {hasNotes ? <SeasonModelNote notes={seasonModel.Notes} variant="card" /> : null}
            </CardWrapper>
        );
    }

    return (
        <TableWrapper className={isCancelled ? 'cancelled' : ''}>
            {tableHeaderRow}
            {hasNotes ? <SeasonModelNote notes={seasonModel.Notes} /> : null}
        </TableWrapper>
    );
};

export default ShowSeasonModelWallInfo;
